import React, {useEffect, useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {Link} from 'react-router-dom';
import {Carousel} from 'react-responsive-carousel';
import {useTranslation} from 'react-i18next';
import {FaApple, FaGoogle, FaGooglePlay, FaRegStar, FaStar} from 'react-icons/fa';
import {httpsCallable} from 'firebase/functions';
import {functions} from '../firebase';
import i18n from '../i18n';
import {useInView} from 'react-intersection-observer';

// Custom Hook for description inView
const useDescriptionInView = () => {
    const [ref, inView] = useInView({threshold: 0.2, triggerOnce: true});
    return {ref, inView};
};


const fadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const slideIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const scaleIn = keyframes`
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
`;

const stretch = keyframes`
    from {
        width: 0;
    }
    to {
        width: 100%;
    }
`;

const HeroSection = styled.div`
    position: relative;
    width: 100%;
    margin-bottom: 40px;
    background-color: ${props => props.theme.colors.background}; /* Sfondo di fallback */
    opacity: 0;
    animation: ${fadeIn} 1s ease-out forwards; /* Animazione di fade-in */
`;

const HeroImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    transform: translateY(${props => props.scrollY * 0.4}px);
    display: block;
`;

const HeroOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 20px;
    z-index: 2;
`;

const HeroTitle = styled.h1`
    color: ${props => props.theme.colors.background};
    font-size: 2.5rem;
    font-weight: 700;
    margin: 0 0 5px;
    animation: ${slideIn} 1s ease-out forwards;
    @media (max-width: 768px) {
        font-size: 1.8rem;
    }
`;

const HeroSubtitle = styled.h2`
    color: ${props => props.theme.colors.background};
    font-size: 1.5rem;
    font-weight: 400;
    margin: 0 0 20px;
    animation: ${slideIn} 1s ease-out forwards;
    animation-delay: 0.2s;
    max-width: 600px;
    line-height: 1.4;
    @media (max-width: 768px) {
        font-size: 1.2rem;
        margin: 0 0 15px;
    }
`;

const HeroButton = styled(Link)`
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
    padding: 12px 24px;
    border-radius: 5px;
    text-decoration: none;
    font-size: 1.1rem;
    font-weight: 500;
    transition: all 0.3s ease;
    animation: ${slideIn} 1s ease-out forwards;
    animation-delay: 0.3s;

    &:hover {
        background-color: ${props => props.theme.colors.secondary};
        transform: scale(1.05);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }

    @media (max-width: 768px) {
        font-size: 0.9rem;
        padding: 10px 20px;
    }
`;

const HomeBodyContainer = styled.div`
    max-width: 1000px;
    width: calc(100% - 40px);
    margin: 0 auto;
    padding: 40px 0;
    display: flex;
    flex-direction: column;
    gap: 40px;
`;

const Divider = styled.div`
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, ${props => props.theme.colors.primary}80, transparent);
    ${props =>
            props.isVisible &&
            css`
                animation: ${stretch} 1s ease-out forwards;
            `};
    @media (max-width: 768px) {
        height: 1px;
    }
`;

const SectionWrapper = styled.div`
    max-width: 800px;
    width: 100%;
    margin: 0 auto;
    padding: 30px;
    border-radius: 15px;
    background: linear-gradient(135deg, ${props => props.theme.colors.background} 0%, ${props => props.backgroundColor || props.theme.colors.primary}22 100%);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    border-top: 3px solid ${props => props.borderColor || props.theme.colors.primary};
    opacity: 0;
    transition: opacity 0.5s ease-out;
    ${props =>
            props.isVisible &&
            css`
                animation: ${slideIn} 0.8s ease-out forwards;
            `}
    box-sizing: border-box;

    @media (max-width: 768px) {
        max-width: 100%;
        width: 100%;
        padding: 20px;
        border-top-width: 2px;
        box-sizing: border-box;
    }
`;

const DescriptionSectionWrapper = styled.div`
    max-width: 800px;
    width: 100%;
    margin: 0 auto;
    padding: 30px;
    border-radius: 10px;
    background: linear-gradient(45deg, ${props => props.theme.colors.background}ee 0%, ${props => props.theme.colors.secondary}11 100%);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    border: 1px solid ${props => props.theme.colors.primary}33;
    opacity: 0;
    transition: all 0.3s ease;
    ${props =>
            props.isVisible &&
            css`
                animation: ${scaleIn} 0.8s ease-out forwards;
            `}
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 20px;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 12px 24px rgba(0, 0, 0, 0.25);
    }

    @media (max-width: 768px) {
        max-width: 100%;
        width: 100%;
        padding: 20px;
        border-width: 1px;
    }
`;

const DescriptionImage = styled.img`
    width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    border: 1px solid ${props => props.theme.colors.primary}33;

    @media (max-width: 768px) {
        height: 150px;
    }
`;

const DescriptionTitle = styled.h3`
    color: ${props => props.theme.colors.primary}dd;
    font-size: 1.7rem;
    margin: 0;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0.5px;
    @media (max-width: 768px) {
        font-size: 1.4rem;
    }
`;

const DescriptionText = styled.p`
    font-size: 1.1rem;
    margin: 0;
    line-height: 1.7;
    color: ${props => props.theme.colors.text}cc;
    @media (max-width: 768px) {
        font-size: 0.95rem;
    }
`;

const DownloadAppSection = styled.div`
    text-align: center;
`;

const DownloadAppTitle = styled.h3`
    color: ${props => props.theme.colors.primary};
    font-size: 1.6rem;
    margin: 0 0 15px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    @media (max-width: 768px) {
        font-size: 1.3rem;
    }
`;

const DownloadAppText = styled.p`
    font-size: 1.2rem;
    margin: 0 0 20px;
    line-height: 1.6;
    color: ${props => props.theme.colors.secondary};
    @media (max-width: 768px) {
        font-size: 1rem;
    }
`;

const DownloadAppButtons = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 15px;
    margin-top: 20px;
`;

const ModalButton = styled.a`
    display: inline-flex;
    align-items: center;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    color: ${props => props.theme.colors.background};
    padding: 12px 24px;
    border-radius: 25px;
    text-decoration: none;
    font-size: 1rem;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3);
    }

    @media (max-width: 768px) {
        font-size: 0.9rem;
        padding: 10px 20px;
    }
`;

const SpinnerContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    background-color: ${props => props.theme.colors.background};
`;

const spin = keyframes`
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
`;

const Spinner = styled.div`
    width: 50px;
    height: 50px;
    border: 5px solid ${props => props.theme.colors.secondary}50;
    border-top: 5px solid ${props => props.theme.colors.primary};
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
`;

const ReviewsSection = styled.div`
    text-align: center;
`;

const ReviewsTitle = styled.h3`
    color: ${props => props.theme.colors.primary};
    font-size: 1.6rem;
    margin: 0 0 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    @media (max-width: 768px) {
        font-size: 1.3rem;
    }
`;

const ReviewsAttribution = styled.p`
    font-size: 0.9rem;
    color: ${props => props.theme.colors.secondary};
    margin: 0 0 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    @media (max-width: 768px) {
        font-size: 0.8rem;
    }
`;

const ReviewItem = styled.div`
    padding: 20px;
    border-radius: 10px;
    background: ${props => props.theme.colors.background}ee;
    margin-bottom: 20px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
        transform: translateY(-5px);
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

const ReviewHeader = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 15px;
`;

const ReviewAuthorContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ReviewAuthorImage = styled.img`
    width: 40px;
    height: 40px;
    object-fit: cover;
    @media (max-width: 768px) {
        width: 32px;
        height: 32px;
    }
`;

const ReviewAuthorPlaceholder = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, ${props => props.theme.colors.secondary}33, ${props => props.theme.colors.primary}33);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
    @media (max-width: 768px) {
        width: 32px;
        height: 32px;
        font-size: 1rem;
    }
`;

const ReviewAuthor = styled.p`
    font-size: 1.2rem;
    font-weight: 600;
    margin: 0;
    color: ${props => props.theme.colors.primary};
    @media (max-width: 768px) {
        font-size: 1rem;
    }
`;

const ReviewStars = styled.div`
    display: flex;
    gap: 6px;
    margin-left: auto;
`;

const ReviewText = styled.p`
    font-size: 1rem;
    margin: 0;
    color: ${props => props.theme.colors.text};
    line-height: 1.6;
    @media (max-width: 768px) {
        font-size: 0.9rem;
    }
`;

const ReviewsFooter = styled.div`
    text-align: center;
    margin-top: 20px;
`;

const StarRating = ({rating}) => {
    const stars = Array.from({length: 5}, (_, index) => (
        <span key={index}>
            {index < rating ? (
                <FaStar style={{color: '#FFD700', fontSize: '1.2rem'}}/>
            ) : (
                <FaRegStar style={{color: '#FFD700', fontSize: '1.2rem'}}/>
            )}
        </span>
    ));
    return <ReviewStars>{stars}</ReviewStars>;
};

const DescriptionItem = ({desc, index, homeImages, descriptionsLength}) => {
    const {ref: descRef, inView: descInView} = useDescriptionInView();
    const {ref: dividerRef, inView: dividerInView} = useInView({threshold: 0.2, triggerOnce: true});

    return (
        <>
            <DescriptionSectionWrapper ref={descRef} isVisible={descInView}>
                <DescriptionImage
                    src={homeImages[index % homeImages.length].url}
                    alt={`Description Image ${index + 1}`}
                    loading="eager"
                />
                <DescriptionTitle>{desc.title}</DescriptionTitle>
                <DescriptionText>{desc.text}</DescriptionText>
            </DescriptionSectionWrapper>
            {index < descriptionsLength - 1 && (
                <Divider ref={dividerRef} isVisible={dividerInView}/>
            )}
        </>
    );
};

const Home = ({rentUser, config, loading, error}) => {
    const {t} = useTranslation();
    const currentLanguage = i18n.language.split('-')[0];

    const [scrollY, setScrollY] = useState(0);
    const [reviews, setReviews] = useState([]);
    const [loadingReviews, setLoadingReviews] = useState(true);
    const [reviewsError, setReviewsError] = useState(null);

    const [downloadRef, downloadInView] = useInView({threshold: 0.2, triggerOnce: true});
    const [reviewsRef, reviewsInView] = useInView({threshold: 0.2, triggerOnce: true});

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (rentUser?.rentId && config?.googlePlaceId) {
            setLoadingReviews(true);
            const fetchReviews = async () => {
                try {
                    const rentUserWebsiteCommentsView = httpsCallable(functions, 'rentUserWebsiteCommentsView');
                    const response = await rentUserWebsiteCommentsView({
                        rentId: rentUser.rentId,
                        googlePlaceId: config.googlePlaceId,
                        language: currentLanguage,
                    });
                    if (response.data.status === 'success') {
                        setReviews(response.data.data);
                    } else {
                        setReviewsError(t('reviewsError', {defaultValue: 'Errore nel caricamento delle recensioni'}));
                    }
                } catch (err) {
                    console.error('Errore nel recupero delle recensioni:', err);
                    setReviewsError(t('reviewsError', {defaultValue: 'Errore nel caricamento delle recensioni'}));
                } finally {
                    setLoadingReviews(false);
                }
            };
            fetchReviews();
        }
    }, [rentUser, config, t, currentLanguage]);

    if (loading || loadingReviews) {
        return (
            <SpinnerContainer>
                <Spinner/>
            </SpinnerContainer>
        );
    }

    if (error || reviewsError) {
        return <div>{error || reviewsError}</div>;
    }

    const googleReviewsUrl = config?.googlePlaceId
        ? `https://search.google.com/local/reviews?placeid=${config.googlePlaceId}`
        : (rentUser?.street && rentUser?.city)
            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${rentUser.name ? rentUser.name + ', ' : ''}${rentUser.street}, ${rentUser.city}`)}`
            : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rentUser?.name || 'bike rental')}`;

    const heroImages = config?.heroImages?.length
        ? config.heroImages
        : [{url: 'https://via.placeholder.com/1200x400'}];

    const homeImages = config?.homeImages?.length
        ? config.homeImages
        : [{url: 'https://via.placeholder.com/800x200'}];

    return (
        <>
            <HeroSection>
                <Carousel
                    autoPlay
                    infiniteLoop
                    showThumbs={false}
                    showStatus={false}
                    showIndicators={true}
                    interval={5000}
                    transitionTime={500}
                    style={{height: '100%'}}
                >
                    {heroImages.map((image, index) => (
                        <div key={index} style={{height: '100%'}}>
                            <HeroImage src={image.url} alt={`Hero ${index + 1}`} scrollY={scrollY} loading="eager"/>
                            <HeroOverlay>
                                <HeroTitle>{config?.heroText || t('heroText')}</HeroTitle>
                                <HeroSubtitle>{config?.subHeroText}</HeroSubtitle>
                                <HeroButton to="/bikes">{t('bookNow')}</HeroButton>
                            </HeroOverlay>
                        </div>
                    ))}
                </Carousel>
            </HeroSection>
            <HomeBodyContainer>
                {config?.isHomeDownloadLocemAvailable && (
                    <>
                        <SectionWrapper
                            ref={downloadRef}
                            isVisible={downloadInView}
                            backgroundColor={props => props.theme.colors.primary}
                            borderColor={props => props.theme.colors.secondary}
                        >
                            <DownloadAppSection>
                                <DownloadAppTitle>{t('downloadAppTitle')}</DownloadAppTitle>
                                <DownloadAppText>{t('downloadAppText')}</DownloadAppText>
                                <DownloadAppButtons>
                                    <ModalButton
                                        href="https://play.google.com/store/apps/details?id=com.locemsolutions.locem"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FaGooglePlay style={{marginRight: '10px'}}/> {t('googlePlay')}
                                    </ModalButton>
                                    <ModalButton
                                        href="https://apps.apple.com/us/app/locem/id6449721104"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <FaApple style={{marginRight: '10px'}}/> {t('appStore')}
                                    </ModalButton>
                                </DownloadAppButtons>
                            </DownloadAppSection>
                        </SectionWrapper>
                        {((config?.descriptions?.length || 0) > 0 || reviews.length > 0) && (
                            <Divider isVisible={downloadInView}/>
                        )}
                    </>
                )}
                {(config?.descriptions || []).map((desc, index) => (
                    <DescriptionItem
                        key={index}
                        desc={desc}
                        index={index}
                        homeImages={homeImages}
                        descriptionsLength={config?.descriptions?.length || 0}
                    />
                ))}
                {((config?.descriptions?.length || 0) > 0) && reviews.length > 0 && (
                    <Divider isVisible={reviewsInView}/>
                )}
                <SectionWrapper
                    ref={reviewsRef}
                    isVisible={reviewsInView}
                    backgroundColor={props => props.theme.colors.primary}
                    borderColor={props => props.theme.colors.secondary}
                >
                    <ReviewsSection>
                        <ReviewsTitle>{t('reviewsTitle', {defaultValue: 'Cosa dicono i nostri clienti'})}</ReviewsTitle>
                        <ReviewsAttribution>
                            <FaGoogle
                                style={{color: '#4285F4'}}/> {t('poweredByGoogle', {defaultValue: 'Recensioni fornite da Google'})}
                        </ReviewsAttribution>
                        {reviews.length > 0 ? (
                            reviews.map((review, index) => (
                                <ReviewItem key={index}>
                                    <ReviewHeader>
                                        <ReviewAuthorContainer>
                                            {review.profile_photo_url ? (
                                                <ReviewAuthorImage
                                                    src={review.profile_photo_url}
                                                    alt={`${review.author_name}'s profile picture`}
                                                />
                                            ) : (
                                                <ReviewAuthorPlaceholder>
                                                    {review.author_name.charAt(0).toUpperCase()}
                                                </ReviewAuthorPlaceholder>
                                            )}
                                            <ReviewAuthor>{review.author_name}</ReviewAuthor>
                                        </ReviewAuthorContainer>
                                        <StarRating rating={review.rating}/>
                                    </ReviewHeader>
                                    <ReviewText>{review.text}</ReviewText>
                                </ReviewItem>
                            ))
                        ) : (
                            <ReviewText>{t('noReviews', {defaultValue: 'Nessuna recensione disponibile'})}</ReviewText>
                        )}
                        <ReviewsFooter>
                            <ModalButton
                                href={googleReviewsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FaGoogle style={{
                                    marginRight: '10px',
                                    color: '#4285F4'
                                }}/> {t('viewAllReviews', {defaultValue: 'Vedi tutte le recensioni'})}
                            </ModalButton>
                        </ReviewsFooter>
                    </ReviewsSection>
                </SectionWrapper>
            </HomeBodyContainer>
        </>
    );
};

export default Home;
